/**
 * Pure helpers for the API keys page (E15 slice 4): what state a key is in, and the expiry the
 * create form offers. No React and no network, so each rule is testable on its own.
 */

import type { ApiKey } from '../api/client';

export type ApiKeyStatus = 'active' | 'expired' | 'revoked';

/** Revoked wins over expired: a key someone revoked stays "revoked" after its expiry passes. */
export function keyStatus(key: ApiKey, now: Date): ApiKeyStatus {
  if (key.revoked_at) return 'revoked';
  if (key.expires_at && new Date(key.expires_at).getTime() <= now.getTime()) return 'expired';
  return 'active';
}

/** The expiries the create form offers (decision 8 of the E15 spec: optional, with a nudge towards
 *  90 days). `days: null` is a key that never expires. The server accepts at most 366 days. */
export const EXPIRY_CHOICES: { id: string; days: number | null; label: string }[] = [
  { id: '30', days: 30, label: '30 days' },
  { id: '90', days: 90, label: '90 days (recommended)' },
  { id: '365', days: 365, label: '1 year' },
  { id: 'never', days: null, label: 'No expiry' },
];

export const DEFAULT_EXPIRY_ID = '90';

/** The timestamp `days` from `now`, for the create request, or undefined for no expiry. */
export function expiryTimestamp(days: number | null, now: Date): string | undefined {
  if (days === null) return undefined;
  return new Date(now.getTime() + days * 24 * 60 * 60 * 1000).toISOString();
}

export const MAX_LABEL_LENGTH = 60;
